import React from "react";
import { DollarSign, Plus, Trash2, Home, Car } from "lucide-react";
import { SectionTitle, Field, Input, Select, Chip } from "../primitives";
import { CURRENCIES, DURATION_KEYS, PRICING_MODES } from "../constants";

/**
 * Step 6 — Pricing.
 *
 * The In-call / Out-call split is the *primary* pricing surface (the two
 * fields the directory shows on every card). Duration packages remain
 * available for agencies who want to publish tiered rates, but only In-call
 * and/or Out-call is required — at least one of the two, either alone is fine.
 */
export default function Step6Pricing({ form, set }) {
  const setRate = (k, v) => set({ pricing: { ...form.pricing, [k]: v } });

  const addCustom = () =>
    set({ pricing: { ...form.pricing, custom: [...(form.pricing.custom || []),
      { _id: `cp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label: "", price: "" }] } });
  const setCustom = (i, patch) => {
    const next = [...(form.pricing.custom || [])];
    next[i] = { ...next[i], ...patch };
    set({ pricing: { ...form.pricing, custom: next } });
  };
  const removeCustom = (i) => set({ pricing: { ...form.pricing, custom: (form.pricing.custom || []).filter((_, idx) => idx !== i) } });

  const toggleMode = (m) => {
    const arr = form.pricing_modes || [];
    set({ pricing_modes: arr.includes(m) ? arr.filter(x => x !== m) : [...arr, m] });
  };

  // Only allow non-negative numbers or empty (clears the field).
  const parsePrice = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? v : "";
  };

  return (
    <div className="space-y-6">
      <SectionTitle icon={DollarSign} title="Pricing"
        subtitle="Set your In-call and Out-call rates independently — clients trust transparent pricing." />

      <div className="flex items-center gap-3">
        <span className="text-xs text-stone-400">Currency:</span>
        <Select
          value={form.currency}
          onChange={(e) => set({ currency: e.target.value })}
          className="w-28 h-9"
          data-testid="pricing-currency"
        >
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </div>

      {/* PRIMARY: In-call / Out-call ------------------------------------ */}
      <Field
        label="Base rates"
        required
        hint="At least one of In-call or Out-call is required. Leave the other blank if you don't offer it."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-stone-800 bg-stone-950/40 p-3">
            <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-amber-300">
              <Home className="w-3.5 h-3.5" /> In-call price
            </label>
            <p className="text-[11px] text-stone-500 mt-0.5">Clients visit you at your location.</p>
            <div className="mt-2 flex items-center gap-2">
              <Input
                type="number" min={0} step="1"
                value={form.incall_price ?? ""}
                onChange={(e) => set({ incall_price: parsePrice(e.target.value) })}
                placeholder="—"
                data-testid="pricing-incall"
                className="w-full"
              />
              <span className="text-xs text-stone-400 w-10">{form.currency || "EUR"}</span>
            </div>
          </div>

          <div className="rounded-lg border border-stone-800 bg-stone-950/40 p-3">
            <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-amber-300">
              <Car className="w-3.5 h-3.5" /> Out-call price
            </label>
            <p className="text-[11px] text-stone-500 mt-0.5">You travel to the client (hotel, home, event).</p>
            <div className="mt-2 flex items-center gap-2">
              <Input
                type="number" min={0} step="1"
                value={form.outcall_price ?? ""}
                onChange={(e) => set({ outcall_price: parsePrice(e.target.value) })}
                placeholder="—"
                data-testid="pricing-outcall"
                className="w-full"
              />
              <span className="text-xs text-stone-400 w-10">{form.currency || "EUR"}</span>
            </div>
          </div>
        </div>
      </Field>

      <Field label="Duration packages (optional)" hint="Leave blank to skip a tier.">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {DURATION_KEYS.map((d) => (
            <div key={d.k}>
              <label className="text-[11px] text-stone-500">{d.label}</label>
              <Input
                type="number" min={0}
                value={form.pricing[d.k] || ""}
                onChange={(e) => setRate(d.k, e.target.value)}
                placeholder="—"
                data-testid={`pricing-${d.k}`}
              />
            </div>
          ))}
        </div>
      </Field>

      <Field label="Custom packages (optional)">
        <div className="space-y-2">
          {(form.pricing.custom || []).map((cp, i) => (
            <div key={cp._id || `cp-idx-${i}`} className="grid grid-cols-[1fr_120px_auto] gap-2">
              <Input
                value={cp.label}
                onChange={(e) => setCustom(i, { label: e.target.value })}
                placeholder="e.g. Weekend getaway"
                data-testid={`pricing-custom-label-${i}`}
              />
              <Input
                type="number" min={0}
                value={cp.price}
                onChange={(e) => setCustom(i, { price: e.target.value })}
                placeholder="Price"
                data-testid={`pricing-custom-price-${i}`}
              />
              <button
                type="button" onClick={() => removeCustom(i)}
                className="px-3 rounded-lg border border-stone-700 text-rose-300 hover:bg-stone-900"
                data-testid={`pricing-custom-remove-${i}`}
              ><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button
            type="button"
            onClick={addCustom}
            className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200"
            data-testid="pricing-custom-add"
          ><Plus className="w-3.5 h-3.5" /> Add custom package</button>
        </div>
      </Field>

      <Field label="Where do you meet?" required>
        <div className="flex flex-wrap gap-2">
          {PRICING_MODES.map(m => (
            <Chip
              key={m.v}
              active={(form.pricing_modes || []).includes(m.v)}
              onClick={() => toggleMode(m.v)}
              testid={`pricing-mode-${m.v}`}
            >{m.label}</Chip>
          ))}
        </div>
      </Field>
    </div>
  );
}
